import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// StudyClockStrip — persistent "now working on" bar shown while a
// Study Clock session is live. Polls /me/study-clock/active on a 30-
// second cadence; renders nothing when no session is running so the
// strip silently disappears between sessions.
//
// While a session is live the strip shows the task title + a count-up
// clock (incremented locally every second so the user sees the timer
// tick between polls). Pause/Resume + Done buttons call the matching
// backend endpoints. On Done, we navigate to /focus where the
// existing review modal handles the rest of the completion flow.
import { notify } from "./Toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
function fmtClock(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const p = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
}
export default function StudyClockStrip() {
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    // Local "ticker offset" — seconds elapsed since the last poll. Reset
    // to 0 on each successful poll. The displayed elapsed time is
    // session.active_seconds + tickerOffset (only when running).
    const [tickerOffset, setTickerOffset] = useState(0);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        try {
            const s = await api.get("/me/study-clock/active");
            setSession(s ?? null);
            setTickerOffset(0);
        }
        catch {
            // Silently swallow — the strip is decorative. A network blip
            // shouldn't bubble up to a toast or error UI.
            setSession(null);
        }
    }, []);
    // Initial fetch + 30s poll. We also listen for a custom event
    // "study-clock:changed" so any /start or /stop elsewhere in the app
    // can force an immediate refresh.
    useEffect(() => {
        void load();
        const id = window.setInterval(load, 30_000);
        const onChange = () => void load();
        window.addEventListener("study-clock:changed", onChange);
        return () => {
            window.clearInterval(id);
            window.removeEventListener("study-clock:changed", onChange);
        };
    }, [load]);
    // Local tick every 1s while running. Paused/no-session = no tick.
    const lastTickRef = useRef(Date.now());
    useEffect(() => {
        if (!session || session.state !== "running")
            return;
        lastTickRef.current = Date.now();
        const id = window.setInterval(() => {
            const now = Date.now();
            const delta = (now - lastTickRef.current) / 1000;
            lastTickRef.current = now;
            setTickerOffset((o) => o + delta);
        }, 1000);
        return () => window.clearInterval(id);
    }, [session?.state, session?.id]);
    if (!session)
        return null;
    const elapsed = session.state === "running"
        ? session.active_seconds + tickerOffset
        : session.active_seconds;
    async function call(action) {
        if (busy)
            return;
        setBusy(true);
        try {
            await api.post(`/me/study-clock/${action}`, {});
            window.dispatchEvent(new Event("study-clock:changed"));
            if (action === "stop") {
                // Send the user to Focus where the Complete? review modal
                // takes over — they get to confirm the actual minutes + pick
                // a completion %.
                navigate("/focus");
            }
            else {
                await load();
            }
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : "Study Clock action failed";
            notify.error(msg);
        }
        finally {
            setBusy(false);
        }
    }
    const paused = session.state === "paused";
    return (_jsxs("div", { className: `study-clock-strip ${paused ? "paused" : "live"}`, children: [_jsx("span", { className: "scs-dot", "aria-hidden": "true" }), _jsx("span", { className: "scs-label", children: paused ? "Paused" : "Live" }), _jsx("span", { className: "scs-title", title: session.title, children: session.title }), _jsx("span", { className: "scs-clock", children: fmtClock(elapsed) }), paused ? (_jsx("button", { type: "button", className: "scs-btn scs-btn-resume", onClick: () => void call("resume"), disabled: busy, title: "Resume the clock", children: "\u25B6 Resume" })) : (_jsx("button", { type: "button", className: "scs-btn scs-btn-pause", onClick: () => void call("pause"), disabled: busy, title: "Pause \u2014 break time won't count toward logged minutes", children: "\u23F8 Pause" })), _jsx("button", { type: "button", className: "scs-btn scs-btn-done", onClick: () => void call("stop"), disabled: busy, title: "Stop the timer and log this session", children: "\u2713 Done" })] }));
}
